// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import {LMModel, LMModelChangedEvent, LMModelField, LMModelStatusEvent} from '@form/model';
import { LMFieldBaseComponent, LMInvalidLPLEvent, LMPreviewEvent } from '@field/lm-field-base.component';
import { LMFilterBuilderService } from '@modal/filter-builder';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, flatMap, takeUntil, takeWhile } from 'rxjs/operators';
import { FilterBuilderDialogResult } from '@utils/app.constants';
import { LMCubeCellRelService } from '@modal/cube-cell-rel/services';
import { LMCubeCellRelationDefinition } from '@modal/cube-cell-rel/lm-cube-cell-rel';
import { LMComputeBuilderService } from '@modal/compute-builder/services';
import { LMDimension, LMMeasure } from '@modal/cubes/lm-cubes';
import { SohoMessageService } from 'ids-enterprise-ng';
import { LMLplEditorService } from '@lplEditor/lm-lpl-editor.service';
import { LMBusclassFieldService } from '@app/components';

@Component({
  selector: 'lm-busclassfield',
  templateUrl: './lm-busclassfield.component.html',
  styleUrls: [ './lm-busclassfield.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMBusclassFieldComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('editorContainer') _editorContainer: ElementRef;

  private unsubscribe$ = new Subject<void>();

  @Input() layoutObject: LMBussClassFieldLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @HostBinding('attr.data-automation-id') bcfAutomationId = '';

  public computeOptionAutomationId = '';
  public derivedOptionAutomationId = '';
  public previewButtonAutomationId = '';
  public builderButtonAutomationId = '';

  rows: number = undefined;

  // Cube Cell Relation
  private dimensionMapping: Array<LMDimension>;
  private measures: Array<LMMeasure>;

  computeType: string;

  customDerivedLpl: string;
  customComputeLpl: string;

  queryValidateEventDebouncer$ = new Subject<string>();
  invalidLPLEventDebouncer$ = new Subject<string>();

  public monacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    theme:           'vs',
    automaticLayout: true,
    language:        'LPL',
    foldingStrategy: 'indentation',
    contextmenu: false,
    minimap: {
      enabled: false
    },
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 2,
    quickSuggestions: false,
    wordWrap: 'on',
    scrollbar: {
      horizontal: 'hidden'
    },
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    scrollBeyondLastLine: false
  };


  // static template variables
  public isBusClassTotal = false;
  public isBusClassGroup = false;
  public isBusClassCompute = false;
  public hideTestMeasures = false;

  public disableDimensions$ = new BehaviorSubject(false);

  private editor: monaco.editor.IEditor;
  private busClassModelField: LMModelField;

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private lmFilterBuilderService: LMFilterBuilderService,
    private lmCubeCellRelService: LMCubeCellRelService,
    private lmComputeBuilderService: LMComputeBuilderService,
    private changeDetectionRef: ChangeDetectorRef,
    private sohoMessageService: SohoMessageService,
    private lmLplEditorService: LMLplEditorService,
    private lmBusclassFieldService: LMBusclassFieldService) {
    super(lmFieldServiceAdapter);
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    this.setVariables();

    if (this.isBusClassTotal) {
      const busClassName = this.layoutObject?.lplFieldInfo?.busClassName ? this.layoutObject.lplFieldInfo.busClassName : 'BusinessClass';

      this.busClassModelField = this.model.getField(busClassName);

      // need to listen to value changes of the Business Class field, so we can correctly enable the Set Dimensions button
      this.subscriptionArray.push(this.busClassModelField.valueChanged.subscribe(() => {
        this.disableDimensions$.next(this.calculateDisableDimensions());
      }));
    }

    this.bcfAutomationId = `lm-${this.fieldAutomationId}-field`;
    this.computeOptionAutomationId = `lm-${this.fieldAutomationId}-compute-field-option`;
    this.derivedOptionAutomationId = `lm-${this.fieldAutomationId}-derived-field-option`;
    this.previewButtonAutomationId = `lm-${this.fieldAutomationId}-preview-button`;
    this.builderButtonAutomationId = `lm-${this.fieldAutomationId}-builder-button`;

    if (this.isBusClassCompute) {
      this.determineTypeAndLPL();
    }

    this.rows = (this.layoutObject.lines === undefined || this.layoutObject.lines === 0) ? 4 : this.layoutObject.lines;

    if (this.isBusClassTotal && this.rows < 10) {
      this.rows = 10;
    }
    if (this.specField.isLpl) {
      this._fieldClassArray.push('lm-monospace');
    }

    if (this.displayAsText) {
      this._fieldClassArray.push('lm-text-data hide-white-space');
    } else {
      this.subscribeToInlineError();
    }
    this.setFieldClasses();

    this.queryValidateEventDebouncer$
      .pipe(debounceTime(1250))
      .subscribe((businessClass) => {
        this.validateLplQuery(businessClass);
      });

    this.invalidLPLEventDebouncer$
      .pipe()
      .subscribe((hasLPLValidationError) => {
        this.fieldEvent?.emit(new LMInvalidLPLEvent(event, JSON.parse(hasLPLValidationError)));
      });

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {
        if (modelChangedEvent.eventType === 'set') {
          this.handleModelChanged();

          this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;
        }
      })
    );

    this.subscriptionArray.push(this.model.statusChanges
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
      .subscribe((modelChangedEvent: LMModelStatusEvent) => {
        // since the lm-lpl-editor doesn't extend LMFieldBase, we need to recalculate read only for the editor here.
        this.updateReadOnlyState();
      })
    );

    this.subscriptionArray.push(this.modelField.valueChanged
      .subscribe(() => {
        this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;
      })
    );
  }

  private handleModelChanged() {
    // need to set input on the component for the change detection to work!
    if (this.editor) {
      const model = this.editor.getModel() as monaco.editor.ITextModel;
      model.setValue(this.modelField.value ? this.modelField.value : '');
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // Set the soho alert id so the lpl error messages with quotes and caret display correctly.
    // TODO: Not sure if condition error can use it or not.
    if (this.isBusClassTotal) {
      if (this.sohoAlert) {
        this.sohoAlert.id = this.id;
      }
    }
    this.initMonaco();
    this.setVariables();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.queryValidateEventDebouncer$.next('');
    this.queryValidateEventDebouncer$.complete();
    this.disableDimensions$.next(false);
    this.disableDimensions$.complete();

    if (this.editor) {
      this.editor.dispose();
    }
  }

  public contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  }

  public onBuildButtonEvent() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.openBuilder(businessClass);
    }
  }

  private openBuilder(businessClass: string) {
    if (this.layoutObject.lplFieldInfo?.mode === 'DerivedField') {
      const builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClass,
        this.layoutObject.lplValidationUrl,
        this.model.formResourceId,
        this.modelField.value);

      builderResult$.pipe(
        takeUntil(this.unsubscribe$),
        distinctUntilChanged()
      ).subscribe((result: LMComputeBuilderResult) => {

        if (result.action === 'Ok') {
          this.modelField.value = result.lplQuery;
          this.model.setDirty(this.layoutObject.bindId, true);
          this.handleModelChanged();
        }
      });
    } else {
      this.lmFilterBuilderService.openBuilderDialog(businessClass, this.layoutObject.fieldLookUpUrl,
        this.layoutObject.fieldSearchUrl, this.layoutObject.lplValidationUrl, this.modelField.value).pipe(
        takeUntil(this.unsubscribe$),
        takeWhile((result: LMFilterBuilderResult) => (result.lplQuery && result.lplQuery !== this.modelField.value)),
        flatMap(result => {
          const lplFieldPicker  = result.lplQuery;

          if (result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_OK && !this.modelField.value) {
            return of(lplFieldPicker);
          }

          if (result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_APPEND) {
            return of(`${this.modelField.value} ${result.matchOption} ${result.lplQuery}`);
          }

            // dialogResult is null if there's no this.modelField.value
          if (!result.dialogResult || result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_OVERWRITE) {
            return of(result.lplQuery);
          }

        })).subscribe((value: string) => {
        this.modelField.value = value;
        this.handleModelChanged();
        this.model.setDirty(this.layoutObject.bindId, true);
        this.changeDetectionRef.markForCheck();
      });
    }
  }

  onPreviewButtonEvent() {
    const businessClass = this.getBusinessClass();
    if ((this.modelField.value as string)?.trim()?.length > 0) {
      if (this.validateBusinessClass(businessClass)) {
        this.showPreview(businessClass);
      }
    } else {
      this.sohoAlert.addInlineMessage(Soho.Locale.translate('BusClassFieldRequired'));
    }
  }

  private showPreview(businessClass: string) {
    this.fieldEvent.emit(new LMPreviewEvent(event, this.modelField.value, businessClass, this.layoutObject.bindId));
  }

  onBuildCubeRelation() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runBuildCubeRelation(businessClass);
    }
  }

  private runBuildCubeRelation(businessClass) {
    const busClassTotalName: string = <string> this.model.getData().fields['BusinessClassTotal'].value;
    const definition: string = this.modelField.value;
    const dimensionMapping: Array<LMDimension> = (this.model.isDirty) ? undefined : this.dimensionMapping;
    // Open the dialog if the business class is not empty
    this.lmCubeCellRelService.buildCubeCellRelDimensionsDialog(this.placeHolder, busClassTotalName, businessClass, definition, dimensionMapping)
      .subscribe((response: LMCubeCellRelationDefinition) => {
        if (response && response.relationLpl && response.relationLpl.length > 0) {
          this.modelField.value = response.relationLpl;
          this.model.setDirty(this.layoutObject.bindId, true);
          this.handleModelChanged();
        }
        if (response && response.dimensionMapping && !this.model.isCreateAction) {
          this.dimensionMapping = response.dimensionMapping;
        }
      });
  }

  onAfterResetDirty(event: SohoTrackDirtyEvent) {
    if (this.isBusClassCompute) {
      this.customDerivedLpl = '';
      this.customComputeLpl = '';

      this.determineTypeAndLPL();
    }

    this.changeDetectionRef.detectChanges();

    super.onAfterResetDirty(event);
  }

  onPreviewCubeRelation() {
    // Run the test only if the form is not dirty.
    if (this.model.isDirty || this.model.isCreateAction) {
      return;
    }
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runPreviewCubeRelation(businessClass);
    }
  }

  private runPreviewCubeRelation(businessClass: string) {
    const busClassTotalName: string = <string> this.model.getData().fields['BusinessClassTotal'].value;
    this.lmCubeCellRelService.buildCubeCellRelMeasuresTestDialog(this.placeHolder, busClassTotalName, businessClass, this.measures)
      .subscribe((measures: Array<LMMeasure>) => {
        // Save it off so that we don't have to go back to fetch.
        this.measures = measures;
      });
  }

  private determineTypeAndLPL() {
    this.computeType = this.model.getData().fields[this.bindId].customComputeType;

    if (this.computeType === 'derived') {
      this.customDerivedLpl = this.modelField.value;
    } else {
      this.customComputeLpl = this.modelField.value;
    }

    if (!this.computeType) {
      this.computeType = 'compute';
    }
  }

  handleComputeTypeChange(event) {

    // change is getting triggered twice, once natively and once from the soho-radio button component. We
    // only want to do things when the change is coming from soho-radio button, so make sure there is
    // an originalEvent on the event
    if (event.originalEvent) { // Added checked handler, change event being triggered twice.
      // Store model field value
      if (this.computeType === 'compute') {
        this.customDerivedLpl = this.modelField.value;
      } else {
        this.customComputeLpl = this.modelField.value;
      }

      // Set new value based on the computeType
      if (this.computeType === 'derived') {
        this.modelField.value = this.customDerivedLpl;
      } else {
        this.modelField.value = this.customComputeLpl;
      }

      if (this.modelField.value) {
        this.model.setDirty(this.layoutObject.bindId, true);
      }

      // always validate the lpl, since this will clear an error
      // when switching between compute and derived, but only
      // do the call to back end if a there is a modelField.value
      this.validateLplQuery(this.getBusinessClass());

      this.handleModelChanged();
    }
  }

  onComputeBuildButtonEvent() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runComputeBuilder(businessClass);
    }
  }

  private runComputeBuilder(businessClassName) {
    let builderResult$: Observable<LMComputeBuilderResult>;
    if (this.computeType === 'compute') {
      builderResult$ = this.lmComputeBuilderService.openComputeBuilderDialog(businessClassName,
        this.layoutObject.fieldLookUpUrl,
        this.layoutObject.fieldSearchUrl,
        this.layoutObject.lplValidationUrl,
        false,
        this.isBusClassCompute,
        false,
        this.modelField.value);
    } else {
      builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClassName,
        this.layoutObject.lplValidationUrl,
        this.model.formResourceId,
        this.modelField.value);
    }

    builderResult$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged()
    ).subscribe((result: LMComputeBuilderResult) => {

      if (result.action === 'Ok') {
        this.modelField.value = result.lplQuery;
        this.model.setDirty(this.layoutObject.bindId, true);
        this.handleModelChanged();
      }
    });
  }

  private runDerivedFieldBuilder(businessClassName) {
    const builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClassName,
      this.layoutObject.lplValidationUrl,
      this.model.formResourceId,
      this.modelField.value);

    builderResult$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged()
    ).subscribe((result: LMComputeBuilderResult) => {

      if (result.action === 'Ok') {
        this.modelField.value = result.lplQuery;
        this.model.setDirty(this.layoutObject.bindId, true);
        this.handleModelChanged();
      }
    });
  }

  private initMonaco() {
    if (!this.lmLplEditorService.isMonacoAvailable()) {
      this.lmLplEditorService.loadingMonacoFinished.pipe(first()).subscribe(() => {
        this.initMonaco();
      });
      return;
    }

    this.editor = monaco.editor.create(this._editorContainer.nativeElement, this.monacoOptions);
    this.lmLplEditorService.syncMonacoThemeToSohoTheme();

    const model = this.editor.getModel() as monaco.editor.ITextModel;
    model.setValue(this.modelField.value);

    this.updateReadOnlyState();
    this.addMonacoListeners();
  }

  private updateReadOnlyState(): void {
    if (this.editor) {
      this.recalculateReadOnly();
      this.editor.updateOptions({ readOnly: this.isReadOnly, extraEditorClassName: this.isReadOnly ? 'lm-readonly-editor' : '' });
    }
  }

  private validateLplQuery(businessClass: string) {
    const model = this.editor.getModel() as monaco.editor.ITextModel;
    monaco.editor.setModelMarkers(model, 'LPL', []);
    this.sohoAlert.removeAllMessages();
    this.model.clearFieldError(this.layoutObject.bindId);
    this.invalidLPLEventDebouncer$.next('false');

    if (!businessClass || businessClass.trim().length <= 0) {
      return;
    }

    if (this.modelField.value) {
      let computeType = this.computeType;

      // override, should be more explicit
      if (this.layoutObject.lplFieldInfo?.mode === 'DerivedField') {
        computeType = 'derived';
      }

      this.lmBusclassFieldService.makeRequest(businessClass, this.modelField.value, this.isBusClassCompute, this.isBusClassTotal, computeType)
        .subscribe((result: LMLplValidationResponse) => {
          if (result.validationError) {
            this.sohoAlert.id = 'error-bcf';
            this.model.setError(this.layoutObject.bindId, true);
            this.invalidLPLEventDebouncer$.next('true');
            this.setLPLMarkings(result, model);

            this.sohoAlert.addInlineMessage(result.validationError.errorDescription);
          }
        });
    }
  }

  private addMonacoListeners(): void {
    const model = this.editor.getModel() as monaco.editor.ITextModel;

    model.onDidChangeContent(() => {
      this.modelField.value = model.getValue();
      this.model.evaluateAndApplyDirtyState(this.layoutObject.bindId);
      this.queryValidateEventDebouncer$.next(this.getBusinessClass());
    });
  }

  public setLPLMarkings(response: LMLplValidationResponse, model: monaco.editor.ITextModel) {
    const markers: monaco.editor.IMarkerData[] = new Array<monaco.editor.IMarkerData>();
    if (response.validationError) {
      const alertType = response.validationError.isWarning ? 'info' : 'error';
      markers.push({
        startLineNumber: response.validationError.row,
        startColumn: response.validationError.startOffset,
        endLineNumber: response.validationError.row,
        endColumn: response.validationError.endOffset,
        message: this.lmLplEditorService.htmlEntityDecode(
          response.validationError.errorDescription
        ),
        severity: alertType === 'error' ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
      });
    }
    monaco.editor.setModelMarkers(model, 'LPL', markers);
  }

  private getBusinessClass(): string {
    // lpltext field: BusinessClassCompute, BusinessClassGroup, BusinessClassTotal
    if (!this.layoutObject.lplFieldInfo) {
      return this.getBusinessClassFromDataView('BusinessClass');
    } else {
      // lpl editor condition builder
      if (this.layoutObject.lplFieldInfo.isRelatedField) {
        return this.getBusinessClassFromDataView(this.layoutObject.lplFieldInfo.busClassName);
      } else {
        return this.layoutObject.lplFieldInfo.busClassName;
      }
    }
  }

  private getBusinessClassFromDataView(fieldName: string): string {
    const businessClassFld = this.model.getData().fields[fieldName];
    return !businessClassFld ? '' : businessClassFld.value as string;
  }

  private validateBusinessClass(businessClass: string): boolean {
    if (!businessClass) {
      const buttons = [  { text: Soho.Locale.translate('Ok'), click: (e, modal) => {modal.close(true);} } ];
      this.sohoMessageService
        .error()
        .message(' ')
        .title(Soho.Locale.translate('BusinessClassRequiredMessage'))
        .buttons(buttons)
        .open();

      return false;
    } else {
      return true;
    }
  }

  private setVariables(): void {
    this.isBusClassTotal = this.layoutObject.componentClass === 'BusinessClassTotal';

    this.isBusClassGroup = this.layoutObject.componentClass === 'BusinessClassGroup';

    this.isBusClassCompute = this.layoutObject.componentClass === 'BusinessClassCompute';

    this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;

    this.disableDimensions$.next(this.calculateDisableDimensions());
  }

  private calculateDisableDimensions(): boolean {
    if (this.isReadOnly) {
      return true;
    }

    const businessClass = this.getBusinessClass();
    return !businessClass || businessClass.length === 0;
  }
}


